import { Check, Coins, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import { useLanguage, useT } from "../i18n";
import { useServiceText } from "../service-text";
import type { DashboardData, Service } from "../types";
import { formatCredits } from "../utils";

/**
 * Inline rate editor, managers only.
 *
 * It lives on the catalogue rather than a settings screen because this is where
 * you are already looking at the rate card and comparing it to the standard price.
 * Sending someone elsewhere to change a number they are staring at is how a
 * feature ends up unused — which is what happened when this shipped API-only.
 */
function RateEditor({ service, onSaved }: { service: Service; onSaved: () => void }) {
  const t = useT();
  // Its own, rather than a prop threaded from the page: the toast below names the
  // service, and naming it in English inside an Arabic toast is the leak this
  // whole pass exists to close.
  const st = useServiceText();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(service.credits));
  const [busy, setBusy] = useState(false);
  const custom = Boolean(service.hasCustomRate);

  async function save(credits: number | null) {
    setBusy(true);
    try {
      await dashboardApi.setRate(service.id, credits);
      toast.success(credits === null
      ? t("toast.rateReset", { name: st.name(service.id, service.name) })
      : t("toast.rateSet", { name: st.name(service.id, service.name), credits }));
      setEditing(false);
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.saveRate"));
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <div className="rate-cell">
        <strong>{formatCredits(service.credits)}</strong>
        {custom && service.standardCredits !== undefined && (
          <span className="rate-standard">standard {formatCredits(service.standardCredits)}</span>
        )}
        <button type="button" className="rate-edit" onClick={() => { setValue(String(service.credits)); setEditing(true); }}>
          {custom ? t("catalogue.changeRate") : t("catalogue.setRate")}
        </button>
      </div>
    );
  }

  return (
    <div className="rate-cell editing">
      <input
        type="number" min="0" step="0.5" value={value} autoFocus disabled={busy}
        onChange={(event) => setValue(event.target.value)}
        aria-label={t("aria.creditsFor", { name: service.name })}
      />
      <button type="button" className="button button-table" disabled={busy} onClick={() => void save(Number(value))}>{t("common.save")}</button>
      <button type="button" className="rate-edit" disabled={busy} onClick={() => setEditing(false)}>{t("common.cancel")}</button>
      {custom && (
        // Clears the exception rather than writing today's standard price in, so a
        // later change to the standard rate still reaches this client.
        <button type="button" className="rate-edit" disabled={busy} onClick={() => void save(null)}>{t("catalogue.useStandard")}</button>
      )}
    </div>
  );
}

export default function CatalogPage({
  services,
  categories,
  onRequest,
  account,
  canPrice,
  onRateChanged,
}: {
  services: Service[];
  categories: string[];
  onRequest: (serviceId: string) => void;
  /** This client's own terms. Hardcoding them printed Zain's rate to every client. */
  account: DashboardData["account"];
  /** Managers only — the server enforces it regardless of what this says. */
  canPrice: boolean;
  onRateChanged: () => void;
}) {
  const t = useT();
  // Names, categories, units and descriptions all come from the database, so they
  // are resolved here rather than translated — see src/service-text.ts.
  const st = useServiceText();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const filtered = useMemo(() => services.filter((service) => (
    // st.haystack carries BOTH languages: somebody reading the Arabic catalogue
    // still types "reel", and somebody on the English one still pastes an Arabic
    // name out of a chat.
    (!search || st.haystack(service).includes(search.toLowerCase()))
    && (!category || service.category === category)
  )), [category, search, services, st]);
  const language = useLanguage();
  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => st.category(a).localeCompare(st.category(b), language)),
    [categories, language, st],
  );

  return (
    <div className="page-stack">
      <section className="catalog-summary">
        <div>
          <span className="section-eyebrow">{t("catalogue.eyebrow")}</span>
          <h2>{t("catalogue.title")}</h2>
          <p>{t("catalogue.intro")}</p>
        </div>
        <div className="catalog-basis">
          <Coins size={22} />
          {/* Credits are the unit of account here (BM, 2026-08-19). This said
              "1 credit = 50 KD · 69 credits allocated monthly" as fixed text,
              which was Zain's contract shown to whoever was looking. */}
          <div><strong>{t("catalogue.monthly", { n: formatCredits(account.monthlyCredits) })}</strong><span>{t("catalogue.includedDeduct")}</span></div>
        </div>
      </section>

      <section className="table-toolbar">
        <label className="search-control">
          <Search size={18} />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("catalogue.search")} aria-label={t("catalogue.search")} />
        </label>
        <label className="filter-control">
          <span className="sr-only">{t("catalogue.category")}</span>
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">{t("catalogue.allCategories")}</option>
            {/* The VALUE stays the English category — it is what the filter above
                compares against, and what the request form keys its icons off. Only
                the label the reader sees is translated.

                Sorted by the LABEL, not by the value. The server returns them in
                English alphabetical order, which in Arabic is no order at all — a
                list that looks alphabetical and is not reads as a broken sort. */}
            {sortedCategories.map((item) => <option key={item} value={item}>{st.category(item)}</option>)}
          </select>
        </label>
        <span className="result-count">{t("catalogue.count", { n: filtered.length })}</span>
      </section>

      <section className="panel catalogue-panel">
        <div className="responsive-table-wrap">
          <table className="data-table catalogue-table">
            <thead><tr><th>{t("catalogue.colService")}</th><th>{t("catalogue.colCategory")}</th><th>{t("catalogue.colUnit")}</th><th>{t("catalogue.colCredits")}</th><th><span className="sr-only">{t("catalogue.colAction")}</span></th></tr></thead>
            <tbody>
              {filtered.map((service) => (
                <tr key={service.id}>
                  <td data-label={t("catalogue.colService")}><strong>{st.name(service.id)}</strong><span>{st.description(service.id)}</span></td>
                  <td data-label={t("catalogue.colCategory")}><span className="category-label">{st.category(service.category)}</span></td>
                  <td data-label={t("catalogue.colUnit")}>{st.unit(service.id)}</td>
                  <td data-label={t("catalogue.colCredits")}>
                    {service.included
                      ? <span className="included-label"><Check size={15} /> {t("catalogue.included")}</span>
                      : canPrice
                        ? <RateEditor service={service} onSaved={onRateChanged} />
                        : <strong>{formatCredits(service.credits)}</strong>}
                  </td>
                  <td data-label={t("catalogue.colAction")}>
                    <button className="button button-table" onClick={() => onRequest(service.id)} title={t("catalogue.requestThis", { service: st.name(service.id) })}>
                      <Plus size={16} /> {t("catalogue.request")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!filtered.length && <div className="empty-state"><Search size={28} /><h4>{t("catalogue.emptyTitle")}</h4><p>{t("catalogue.emptyBody")}</p></div>}
      </section>
    </div>
  );
}
